import React, { useState, useEffect, ReactNode } from 'react'
import Icon from '@pathccm/rula-ui/icon'
import '@pathccm/rula-ui/animations.css'
import { Sky } from '@pathccm/rula-ui/colors'
import AvobEntryFormInfoModal from './AvobEntryFormInfoModal'

interface Props {
  src: string,
}

const EntryForm: React.FC<Props> = (props) => {
  const [height, setHeight] = useState(440)
  const [url, setUrl] = useState<URL | undefined>(undefined)
  const [loading, setLoading] = useState(true)
  const [showOverlay, setShowOverlay] = useState(true)
  const [showAvobInfoModal, setShowAvobInfoModal] = useState(false)
  const [selectedInsuranceAvobDisplayName, setSelectedInsuranceAvobDisplayName] = useState()
  const containerRef = React.useRef<HTMLDivElement>(null);
  const entryFormIframeId = 'entry-form-iframe'
  const fadeOutTime = 150

  const onMessageReceivedFromIframe = (event): void => {
    if (event.data.height !== undefined) {
      setHeight(event.data.height)
    }
    if (event.data.type === 'OPEN_AVERAGE_COST_INFO_MODAL') {
      setShowAvobInfoModal(true)
      setSelectedInsuranceAvobDisplayName(event.data.payload)
    }
  }

  useEffect(() => {
    window.addEventListener('message', onMessageReceivedFromIframe)

    const newUrl = new URL(props.src)
    const parentParams = new URLSearchParams(location.search)

    newUrl.search = new URLSearchParams([...newUrl.searchParams, ...parentParams]).toString()
    newUrl.searchParams.append('parentUrl', location.href)

    // Used for testing modal for before experiment goes live
    const isRulaEmployee = parentParams.get("isRulaEmployee")?.toLowerCase() === 'true' ? true : false
    if (isRulaEmployee) {
      newUrl.searchParams.append('isRulaEmployee', 'true')
    }

    // We want to only show the AVOB component on the home page; we let the entry form know
    // we're on the home page via the 'pageContext' parameter
    const isHomepage = window.location.pathname === '/' || window.location.pathname === ''
    if (isHomepage) {
      newUrl.searchParams.append('pageContext', 'homepage')
    }

    setUrl(newUrl)

    return () => {
      window.removeEventListener('message', onMessageReceivedFromIframe)
    }
  }, [])

  useEffect(() => {
    if (!loading) {
      setTimeout(() => {
        setShowOverlay(false)
      }, fadeOutTime)
    }
  }, [loading])

  // Send message down to entry form iframe when user clicks outside of it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        const iframe = document.getElementById(entryFormIframeId) as HTMLIFrameElement;
        iframe?.contentWindow?.postMessage({ type: 'CLICK_OUTSIDE_IFRAME' }, 'https://entry.rula.com');
      }
    };
    window.addEventListener("mousedown", handleClickOutside);
    return () => {
      window.removeEventListener("mousedown", handleClickOutside);
    };
  }, [containerRef]);

  return (
    <div className='entry-form' ref={containerRef}>
      {showOverlay
        ? (
          <div className='loading-iframe' style={{ opacity: loading ? '1' : '0' }}>
            <Icon icon='Spinner' className='spin' size='large' color={Sky.$50} />
          </div>
        )
        : <></>}
      <AvobEntryFormInfoModal
        isOpen={showAvobInfoModal}
        onOpenChange={setShowAvobInfoModal}
        avobDisplayName={selectedInsuranceAvobDisplayName}
      />
      <iframe
        title='entry form iframe'
        id={entryFormIframeId}
        height={`${height}px`}
        src={url?.toString()}
        onLoad={() => setLoading(false)}
      />
    </div>
  )
}

export default EntryForm
